import { useEffect, useState } from "react";

import { IUseGraphic } from "./IUseGraphic";
import { PlacementAgent, PlacementAgentValues } from "../../Models";

const QUALITAS_INSTITUTION_ID = 536;

const EMPTY_PERCENT = {
    confirmed: "0",
    pre: "0",
    disp: "0"
};

const buildVehicleSummary = (vehicles: PlacementAgentValues[]): PlacementAgentValues[] => {
    const groupedVehicles = new Map<number, PlacementAgentValues>();

    vehicles
        .filter((vehicle) => vehicle.active)
        .forEach((vehicle) => {
            const currentVehicle = groupedVehicles.get(vehicle.vehicleId);
            if (currentVehicle) {
                currentVehicle.confirmed += vehicle.confirmed ?? 0;
                currentVehicle.reserved += vehicle.reserved ?? 0;
                currentVehicle.notConfirmed += vehicle.notConfirmed ?? 0;
                currentVehicle.total += vehicle.total ?? 0;
                currentVehicle.ticket += vehicle.ticket ?? 0;
                currentVehicle.available += vehicle.available ?? 0;
                currentVehicle.maxAvailable += vehicle.maxAvailable ?? 0;
                currentVehicle.active = currentVehicle.active || vehicle.active;
                return;
            }

            groupedVehicles.set(vehicle.vehicleId, { ...vehicle });
        });

    const summarizedVehicles = Array.from(groupedVehicles.values());
    const totalRow = summarizedVehicles.reduce<PlacementAgentValues>((accumulator, vehicle) => ({
        ...accumulator,
        confirmed: accumulator.confirmed + (vehicle.confirmed ?? 0),
        reserved: accumulator.reserved + (vehicle.reserved ?? 0),
        notConfirmed: accumulator.notConfirmed + (vehicle.notConfirmed ?? 0),
        total: accumulator.total + (vehicle.total ?? 0),
        ticket: accumulator.ticket + (vehicle.ticket ?? 0),
        available: accumulator.available + (vehicle.available ?? 0),
        maxAvailable: accumulator.maxAvailable + (vehicle.maxAvailable ?? 0),
    }), {
        confirmed: 0,
        reserved: 0,
        notConfirmed: 0,
        total: 0,
        ticket: 0,
        available: 0,
        maxAvailable: 0,
        vehicleLegalName: "All",
        vehicleId: -1,
        vehicleShortName: "All",
        active: true,
    });

    return [totalRow, ...summarizedVehicles];
};

export const useGraphic = ({
    userIdInstitution,
    placementAgents,
    setSelectedInstitutionId,
    setSelectedVehicleId
}: IUseGraphic) => {
    
    const [tabItems, setTabItems] = useState<PlacementAgentValues[]>([]);
    const [selectedInstitution, setSelectedInstitution] = useState<number>(0)
    const [pieData, setPieData] = useState();
    const [selectedVehicle, setSelectedVehicle] = useState<number>(0)
    const [percent, setPercent] = useState(EMPTY_PERCENT);

    useEffect(()=>{
        if(placementAgents) {
            if (placementAgents?.length > 0) {
                const defaultInstitutionId = userIdInstitution === QUALITAS_INSTITUTION_ID ? 0 : userIdInstitution;

                setSelectedInstitution(defaultInstitutionId);
                setSelectedInstitutionId(defaultInstitutionId);
                setSelectedVehicleId(null);
                getContractValues(defaultInstitutionId, 0);
            }
        }
    },[placementAgents, setSelectedInstitutionId, setSelectedVehicleId, userIdInstitution])

    const onChangeInstitution = (e: number) => {
        setSelectedInstitution(e)
        setSelectedInstitutionId(e)
        setSelectedVehicle(0)
        setSelectedVehicleId(null)
        getContractValues(e, 0)
    };
    
    const onChangeVehicle = (idInstitution: number, vehicleIndex: number) => {
        setSelectedVehicle(vehicleIndex)
        setSelectedInstitutionId(idInstitution);
        setSelectedVehicleId(tabItems[vehicleIndex].vehicleId === -1 ? null : tabItems[vehicleIndex].vehicleId);
    }

    const getContractValues = (idInstitution: number | string, vehicleIndex: number) => {
        if (placementAgents.length > 0) {
            setPercent(EMPTY_PERCENT);
            setTabItems([])
            const allVehicles = idInstitution.toString() === "0"
                ? placementAgents.flatMap((placementAgent) => placementAgent.institutionValues ?? [])
                : placementAgents.find((x: PlacementAgent) => x.idInstitution.toString() === idInstitution.toString())?.institutionValues ?? [];

            const newData = buildVehicleSummary(allVehicles);
            if (newData.length > 0) {
                setTabItems(newData);
                const res = newData[vehicleIndex] ?? newData[0];
                let pieData: any = [
                    {type: "Total Firmado",
                    value: res.confirmed !== null ? res.confirmed : 0},
                    {type: "Total en Proceso",
                    value: res.reserved !== null ? res.reserved : 0},
                    {type: "Total Disponible",
                    value: res.available !== null ? res.available : 0
                }]
                setPercent({
                    confirmed: (res.maxAvailable > 0 ? (((res.confirmed !== null ? res.confirmed : 0) / res.maxAvailable) * 100) : 0).toFixed(2),
                    pre: (res.maxAvailable > 0 ? (((res.reserved !== null ? res.reserved : 0) / res.maxAvailable) * 100) : 0).toFixed(2),
                    disp: (res.maxAvailable > 0 ? (((res.available !== null ? res.available : 0) / res.maxAvailable) * 100) : 0).toFixed(2)
                })
                setPieData(pieData);
            }
        }
    };

    return {
        getContractValues,
        pieData,
        percent,
        tabItems,
        onChangeInstitution,
        selectedInstitution,
        setSelectedInstitution,
        onChangeVehicle,
        selectedVehicle
    }
}
